import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Avatar, Tooltip, Typography } from "@material-ui/core";
import GroupIcon from "@material-ui/icons/Group";
import { Done, HighlightOff, Replay, SwapHoriz } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { toast } from "react-toastify";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "58px minmax(0, 1fr) auto",
    gap: 12,
    alignItems: "center",
    minHeight: 92,
    margin: "8px 10px",
    padding: "12px 14px",
    borderRadius: 14,
    border: `1px solid ${theme.mode === "light" ? "#e4eaf2" : "rgba(255,255,255,0.12)"}`,
    background: theme.mode === "light" ? "#ffffff" : theme.palette.background.paper,
    boxShadow: theme.mode === "light"
      ? "0 3px 12px rgba(15, 23, 42, 0.045)"
      : "0 3px 12px rgba(0,0,0,0.20)",
    transition: "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.mode === "light" ? "#d2dbea" : "rgba(255,255,255,0.22)",
      boxShadow: theme.mode === "light"
        ? "0 7px 18px rgba(15, 23, 42, 0.07)"
        : "0 7px 18px rgba(0,0,0,0.28)",
      transform: "translateY(-1px)"
    },
    "&.Mui-selected": {
      background: theme.mode === "light" ? "#fbfdff" : theme.palette.background.default,
      borderColor: theme.palette.primary.main,
      boxShadow: theme.mode === "light"
        ? "0 7px 20px rgba(25,118,210,.12)"
        : "0 7px 20px rgba(25,118,210,.20)"
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "50px minmax(0, 1fr) auto",
      gap: 9,
      minHeight: 88,
      padding: "10px 10px",
      margin: "7px 7px"
    }
  },
  pendingTicket: {
    cursor: "unset"
  },
  avatarBox: {
    position: "relative",
    width: 58,
    height: 58,
    alignSelf: "center",
    [theme.breakpoints.down("sm")]: {
      width: 50,
      height: 50
    }
  },
  avatar: {
    width: 58,
    height: 58,
    boxShadow: "0 3px 10px rgba(15,23,42,.08)",
    [theme.breakpoints.down("sm")]: {
      width: 50,
      height: 50
    }
  },
  channelBadge: {
    position: "absolute",
    right: -3,
    bottom: -2,
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 7px rgba(15,23,42,.13)",
    "& svg": {
      margin: "0 !important",
      fontSize: "20px !important"
    }
  },
  content: {
    minWidth: 0,
    alignSelf: "center"
  },
  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
    marginBottom: 3
  },
  contactName: {
    fontSize: "0.94rem",
    lineHeight: 1.15,
    fontWeight: 800,
    color: theme.mode === "light" ? "#172033" : "#f8fafc",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  message: {
    fontSize: "0.84rem",
    lineHeight: 1.25,
    color: theme.mode === "light" ? "#59667a" : "#b8c2d1",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%"
  },
  messageUnread: {
    fontWeight: 700,
    color: theme.mode === "light" ? "#273244" : "#fff"
  },
  tagsRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 24,
    padding: "5px 11px",
    borderRadius: 999,
    fontSize: "0.67rem",
    lineHeight: 1,
    fontWeight: 800,
    textTransform: "uppercase",
    whiteSpace: "nowrap"
  },
  right: {
    minWidth: 96,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 8,
    [theme.breakpoints.down("sm")]: {
      minWidth: 88
    }
  },
  timeRow: {
    display: "flex",
    alignItems: "center",
    gap: 7
  },
  time: {
    fontSize: "0.81rem",
    color: theme.mode === "light" ? "#526178" : "#cbd5e1",
    fontWeight: 500
  },
  timeUnread: {
    color: "#159447",
    fontWeight: 800
  },
  unread: {
    minWidth: 22,
    height: 22,
    borderRadius: 999,
    padding: "0 6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#25b84f",
    color: "#fff",
    fontSize: "0.70rem",
    fontWeight: 800
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8
  },
  actionButton: {
    minWidth: "38px !important",
    width: 38,
    height: 38,
    padding: "0 !important",
    borderRadius: "50% !important",
    boxShadow: "none !important",
    border: "1px solid transparent !important",
    "& .MuiSvgIcon-root": {
      fontSize: 21
    },
    "&:hover": {
      boxShadow: "none !important"
    }
  },
  acceptAction: {
    color: "#16a34a !important",
    background: "#e6f9ed !important",
    borderColor: "#d4f3df !important"
  },
  transferAction: {
    color: "#2563eb !important",
    background: "#e9f2ff !important",
    borderColor: "#d8e7ff !important"
  },
  closeAction: {
    color: "#ef2b2d !important",
    background: "#ffe9ea !important",
    borderColor: "#ffd9db !important"
  },
  reopenAction: {
    color: "#d97706 !important",
    background: "#fff4dd !important",
    borderColor: "#ffe8bd !important"
  }
}));

const normalizeHex = (value, fallback) => {
  if (!value || typeof value !== "string") return fallback;
  const candidate = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(candidate)) return candidate;
  if (/^#[0-9a-fA-F]{3}$/.test(candidate)) {
    return "#" + candidate.slice(1).split("").map((c) => c + c).join("");
  }
  return fallback;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex, "#7c7c7c").replace("#", "");
  return {
    r: parseInt(normalized.substring(0, 2), 16),
    g: parseInt(normalized.substring(2, 4), 16),
    b: parseInt(normalized.substring(4, 6), 16)
  };
};

const pastelStyle = (hex, fallback) => {
  const color = normalizeHex(hex, fallback);
  const { r, g, b } = hexToRgb(color);
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  const textColor = luminance > 205
    ? `rgb(${Math.max(r - 115, 35)}, ${Math.max(g - 115, 35)}, ${Math.max(b - 115, 35)})`
    : color;

  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.14)`,
    color: textColor,
    border: `1px solid rgba(${r}, ${g}, ${b}, 0.10)`
  };
};

const truncate = (str, len) => {
  if (isNil(str)) return "";
  return str.length > len ? `${str.substring(0, len)}...` : str;
};

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { get: getSetting } = useCompanySettings();

  const [loading, setLoading] = useState(false);
  const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    const setting = await getSetting({ column: "requiredTag" });

    if (setting.requiredTag === "enabled") {
      try {
        const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
        if (!contactTags.data.tags) {
          toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
        } else {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null
          });
          if (isMounted.current) setLoading(false);
          history.push("/tickets/");
        }
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    } else {
      setLoading(true);
      try {
        await api.put(`/tickets/${id}`, {
          status: "closed",
          userId: user?.id || null
        });
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
      if (isMounted.current) setLoading(false);
      history.push("/tickets/");
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
    history.push("/tickets/");
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) setTransferTicketModalOpen(false);
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) setLoading(false);
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status: ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.data.uuid}`);
        }
      } else {
        let setting;
        try {
          setting = await getSetting({ column: "sendGreetingAccepted" });
        } catch (err) {
          toastError(err);
        }

        if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) setLoading(false);
        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;
    try {
      setting = await getSetting({ column: "greetingAcceptedMessage" });
    } catch (err) {
      toastError(err);
    }

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${setting.greetingAcceptedMessage}`.trim()
    };

    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (selectedTicket) => {
    const code = uuidv4();
    const { id, uuid } = selectedTicket;
    setCurrentTicket({ id, uuid, code });
  };

  const stopEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderLastMessage = () => {
    if (!ticket.lastMessage) return <br />;
    if (ticket.lastMessage.includes("data:image/png;base64")) return <MarkdownWrapper>Localização</MarkdownWrapper>;
    if (ticket.lastMessage.includes("BEGIN:VCARD")) return <MarkdownWrapper>Contato</MarkdownWrapper>;
    return <MarkdownWrapper>{truncate(ticket.lastMessage, 70)}</MarkdownWrapper>;
  };

  const renderTag = (label, color, fallback) => (
    <span className={classes.tag} style={pastelStyle(color, fallback)}>{label}</span>
  );

  const renderAction = (title, icon, action, className) => (
    <Tooltip title={title}>
      <span>
        <ButtonWithSpinner
          variant="contained"
          loading={loading}
          className={clsx(classes.actionButton, className)}
          onClick={(e) => {
            stopEvent(e);
            action();
          }}
        >
          {icon}
        </ButtonWithSpinner>
      </span>
    </Tooltip>
  );

  const connectionColor = ticket?.whatsapp?.color || (ticket.channel === "facebook" ? "#4267B2" : ticket.channel === "instagram" ? "#E1306C" : "#25D366");
  const queueColor = ticket?.queue?.color || "#7c7c7c";
  const userColor = ticket?.user?.color || "#111827";
  const selected = Boolean(ticketId && ticketId === ticket.uuid);

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}

      <div
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          "Mui-selected": selected
        })}
        onClick={() => handleSelectTicket(ticket)}
      >
        <div className={classes.avatarBox}>
          <Avatar
            className={classes.avatar}
            src={ticket?.contact?.urlPicture?.startsWith("http") ? ticket.contact.urlPicture : "/nopicture.png"}
          />
          {ticket.channel && (
            <span className={classes.channelBadge}>
              <ConnectionIcon connectionType={ticket.channel} />
            </span>
          )}
        </div>

        <div className={classes.content}>
          <div className={classes.nameRow}>
            {ticket.isGroup && ticket.channel === "whatsapp" && (
              <GroupIcon fontSize="small" style={{ color: theme.mode === "light" ? "#64748b" : "#cbd5e1" }} />
            )}
            {ticket.channel && <ConnectionIcon connectionType={ticket.channel} />}
            <Typography component="span" className={classes.contactName}>
              {truncate(ticket.contact?.name || ticket.contact?.number, 60)}
            </Typography>
          </div>

          <Typography
            component="div"
            className={clsx(classes.message, {
              [classes.messageUnread]: Number(ticket.unreadMessages) > 0
            })}
          >
            {renderLastMessage()}
          </Typography>

          <div className={classes.tagsRow}>
            {ticket?.whatsapp && renderTag(ticket.whatsapp.name.toUpperCase(), connectionColor, "#25D366")}
            {renderTag(
              ticket.queueId ? ticket.queue?.name?.toUpperCase() : ticket.status === "lgpd" ? "LGPD" : "SEM FILA",
              queueColor,
              "#7c7c7c"
            )}
            {ticket?.user && renderTag(ticket.user.name.toUpperCase(), userColor, "#111827")}
            {ticket.tags?.map((tag) => (
              <ContactTag tag={tag} key={`ticket-contact-tag-${ticket.id}-${tag.id}`} />
            ))}
          </div>
        </div>

        <div className={classes.right}>
          <div className={classes.timeRow}>
            {ticket.lastMessage && (
              <Typography
                component="span"
                className={clsx(classes.time, {
                  [classes.timeUnread]: Number(ticket.unreadMessages) > 0
                })}
              >
                {isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
              </Typography>
            )}
            {Number(ticket.unreadMessages) > 0 && (
              <span className={classes.unread}>{ticket.unreadMessages}</span>
            )}
          </div>

          <div className={classes.actions}>
            {ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined) &&
              renderAction(i18n.t("ticketsList.buttons.accept"), <Done />, handleOpenAcceptTicketWithouSelectQueue, classes.acceptAction)}

            {ticket.status === "pending" && ticket.queueId !== null &&
              renderAction(i18n.t("ticketsList.buttons.accept"), <Done />, () => handleAcepptTicket(ticket.id), classes.acceptAction)}

            {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") &&
              renderAction(i18n.t("ticketsList.buttons.transfer"), <SwapHoriz />, handleOpenTransferModal, classes.transferAction)}

            {(ticket.status === "open" || ticket.status === "group") &&
              renderAction(i18n.t("ticketsList.buttons.closed"), <HighlightOff />, () => handleCloseTicket(ticket.id), classes.closeAction)}

            {((ticket.status === "pending" || ticket.status === "lgpd") && (user.userClosePendingTicket === "enabled" || user.profile === "admin")) &&
              renderAction(i18n.t("ticketsList.buttons.ignore"), <HighlightOff />, () => handleCloseIgnoreTicket(ticket.id), classes.closeAction)}

            {ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined) &&
              renderAction(i18n.t("ticketsList.buttons.reopen"), <Replay />, handleOpenAcceptTicketWithouSelectQueue, classes.reopenAction)}

            {ticket.status === "closed" && ticket.queueId !== null &&
              renderAction(i18n.t("ticketsList.buttons.reopen"), <Replay />, () => handleAcepptTicket(ticket.id), classes.reopenAction)}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default TicketListItemCustom;
